import {
  ToolSettingModel,
  ToolTypeInfo,
  GetAllToolTypesResponse,
  CreateToolSettingRequest,
  UpdateToolSettingRequest,
  BulkUpdateToolsRequest,
} from '@app/domain/ToolSettingModel';
import { useAuthHeaders } from '@app/hooks/useAuthHeaders';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosRequestConfig, AxiosRequestHeaders } from 'axios';

export const getPartnerTools = (partnerID: string, headers: AxiosRequestHeaders): Promise<ToolSettingModel[]> => {
  const url = `${process.env.REACT_APP_BASE_URL}/tool-settings/partners/${partnerID}/tools`;
  const config: AxiosRequestConfig = { headers };
  return axios.get<ToolSettingModel[]>(url, config).then(({ data }) => data);
};

export const getEnabledPartnerTools = (
  partnerID: string,
  headers: AxiosRequestHeaders,
): Promise<ToolSettingModel[]> => {
  const url = `${process.env.REACT_APP_BASE_URL}/tool-settings/partners/${partnerID}/tools/enabled`;
  const config: AxiosRequestConfig = { headers };
  return axios.get<ToolSettingModel[]>(url, config).then(({ data }) => data);
};

export const getPartnerTool = (
  partnerID: string,
  toolType: string,
  headers: AxiosRequestHeaders,
): Promise<ToolSettingModel> => {
  const url = `${process.env.REACT_APP_BASE_URL}/tool-settings/partners/${partnerID}/tools/${toolType}`;
  const config: AxiosRequestConfig = { headers };
  return axios.get<ToolSettingModel>(url, config).then(({ data }) => data);
};

export const createOrUpdatePartnerTool = (
  partnerID: string,
  toolData: CreateToolSettingRequest,
  headers: AxiosRequestHeaders,
): Promise<ToolSettingModel> => {
  const url = `${process.env.REACT_APP_BASE_URL}/tool-settings/partners/${partnerID}/tools`;
  const config: AxiosRequestConfig = { headers };
  return axios.post<ToolSettingModel>(url, toolData, config).then(({ data }) => data);
};

export const updatePartnerTool = (
  partnerID: string,
  toolType: string,
  updateData: UpdateToolSettingRequest,
  headers: AxiosRequestHeaders,
): Promise<ToolSettingModel> => {
  const url = `${process.env.REACT_APP_BASE_URL}/tool-settings/partners/${partnerID}/tools/${toolType}`;
  const config: AxiosRequestConfig = { headers };
  return axios.patch<ToolSettingModel>(url, updateData, config).then(({ data }) => data);
};

export const deletePartnerTool = (partnerID: string, toolType: string, headers: AxiosRequestHeaders): Promise<void> => {
  const url = `${process.env.REACT_APP_BASE_URL}/tool-settings/partners/${partnerID}/tools/${toolType}`;
  const config: AxiosRequestConfig = { headers };
  return axios.delete(url, config).then(() => undefined);
};

export const bulkUpdatePartnerTools = (
  partnerID: string,
  bulkData: BulkUpdateToolsRequest,
  headers: AxiosRequestHeaders,
): Promise<ToolSettingModel[]> => {
  const url = `${process.env.REACT_APP_BASE_URL}/tool-settings/partners/${partnerID}/tools/bulk-update`;
  const config: AxiosRequestConfig = { headers };
  return axios.post<ToolSettingModel[]>(url, bulkData, config).then(({ data }) => data);
};

export const getAvailableToolTypes = (headers: AxiosRequestHeaders): Promise<GetAllToolTypesResponse> => {
  const url = `${process.env.REACT_APP_BASE_URL}/tool-settings/tool-types`;
  const config: AxiosRequestConfig = { headers };
  return axios.get<GetAllToolTypesResponse>(url, config).then(({ data }) => data);
};

export const initializeDefaultTools = (
  partnerID: string,
  headers: AxiosRequestHeaders,
): Promise<ToolSettingModel[]> => {
  const url = `${process.env.REACT_APP_BASE_URL}/tool-settings/partners/${partnerID}/tools/initialize-defaults`;
  const config: AxiosRequestConfig = { headers };
  return axios.post<ToolSettingModel[]>(url, null, config).then(({ data }) => data);
};

// React Query hooks
export const usePartnerTools = (partnerID: string) => {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ['partner-tools', partnerID],
    queryFn: () => getPartnerTools(partnerID, headers!),
    enabled: !!headers && !!partnerID,
  });
};

export const useEnabledPartnerTools = (partnerID: string) => {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ['enabled-partner-tools', partnerID],
    queryFn: () => getEnabledPartnerTools(partnerID, headers!),
    enabled: !!headers && !!partnerID,
  });
};

export const usePartnerTool = (partnerID: string, toolType: string) => {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ['partner-tool', partnerID, toolType],
    queryFn: () => getPartnerTool(partnerID, toolType, headers!),
    enabled: !!headers && !!partnerID && !!toolType,
  });
};

export const useAvailableToolTypes = () => {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ['available-tool-types'],
    queryFn: () => getAvailableToolTypes(headers!),
    enabled: !!headers,
  });
};

export const useCreateOrUpdateTool = (partnerID: string) => {
  const queryClient = useQueryClient();
  const headers = useAuthHeaders();
  return useMutation({
    mutationFn: (toolData: CreateToolSettingRequest) => createOrUpdatePartnerTool(partnerID, toolData, headers!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-tools', partnerID] });
      queryClient.invalidateQueries({ queryKey: ['enabled-partner-tools', partnerID] });
    },
  });
};

export const useUpdateTool = (partnerID: string) => {
  const queryClient = useQueryClient();
  const headers = useAuthHeaders();
  return useMutation({
    mutationFn: ({ toolType, updateData }: { toolType: string; updateData: UpdateToolSettingRequest }) =>
      updatePartnerTool(partnerID, toolType, updateData, headers!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-tools', partnerID] });
      queryClient.invalidateQueries({ queryKey: ['enabled-partner-tools', partnerID] });
    },
  });
};

export const useDeleteTool = (partnerID: string) => {
  const queryClient = useQueryClient();
  const headers = useAuthHeaders();
  return useMutation({
    mutationFn: (toolType: string) => deletePartnerTool(partnerID, toolType, headers!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-tools', partnerID] });
      queryClient.invalidateQueries({ queryKey: ['enabled-partner-tools', partnerID] });
    },
  });
};

export const useBulkUpdateTools = (partnerID: string) => {
  const queryClient = useQueryClient();
  const headers = useAuthHeaders();
  return useMutation({
    mutationFn: (bulkData: BulkUpdateToolsRequest) => bulkUpdatePartnerTools(partnerID, bulkData, headers!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-tools', partnerID] });
      queryClient.invalidateQueries({ queryKey: ['enabled-partner-tools', partnerID] });
    },
  });
};

export const useInitializeDefaultTools = (partnerID: string) => {
  const queryClient = useQueryClient();
  const headers = useAuthHeaders();
  return useMutation({
    mutationFn: () => initializeDefaultTools(partnerID, headers!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-tools', partnerID] });
      queryClient.invalidateQueries({ queryKey: ['enabled-partner-tools', partnerID] });
    },
  });
};
